import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useAppSelector } from "../../app/hooks";
import { useDispatch } from "react-redux";
import { fetchTrade } from "../../features/dashboard/tradeSlice";
import Spinner from "react-bootstrap/esm/Spinner";
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  ListGroup,
  Button,
  Badge,
} from "react-bootstrap";
import Collapse from "react-bootstrap/Collapse";
import dayjs from "dayjs";
import "./tradeDayDetail.scss";
import { getDateAndTime } from "../../features/dashboard/util";
// @ts-ignore
import { CandleStickChart } from "../../features/tradeDetail/components/CandleStickChart";

function calculateHeldDuration(openDate, closedDate) {
  const open = dayjs(String(openDate), "YYYYMMDDHHmmss");
  const close = dayjs(String(closedDate), "YYYYMMDDHHmmss");
  const diff = close.diff(open);
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);
  return `${hours}h ${minutes}m ${seconds}s`;
}

function yyyymmdd(val) {
  return dayjs(String(val), "YYYYMMDDHHmmss").format("YYYY-MM-DD");
}

function matchesDate(target, trade) {
  if (yyyymmdd(trade.openDate) === target) return true;
  if (yyyymmdd(trade.closedDate) === target) return true;
  if (trade.singleTrades?.some((t) => yyyymmdd(t.dateTime) === target))
    return true;
  return false;
}

function summarizeDay(trades) {
  let totalPnl = 0;
  let wins = 0;
  let losses = 0;
  let biggestWin = { pnl: Number.NEGATIVE_INFINITY, symbol: "" };
  let biggestLoss = { pnl: Number.POSITIVE_INFINITY, symbol: "" };
  let totalShares = 0;
  let totalCommissions = 0;
  let totalFees = 0;
  let totalBuys = 0;
  let totalSells = 0;

  trades.forEach((tr) => {
    totalPnl += tr.pnl || 0;
    totalShares += tr.totalShares || 0;
    if (tr.pnl > 0) wins += 1;
    if (tr.pnl < 0) losses += 1;
    if (tr.pnl > biggestWin.pnl)
      biggestWin = { pnl: tr.pnl, symbol: tr.symbol?.toUpperCase() || "" };
    if (tr.pnl < biggestLoss.pnl)
      biggestLoss = { pnl: tr.pnl, symbol: tr.symbol?.toUpperCase() || "" };
    tr.singleTrades?.forEach((ex) => {
      totalCommissions += ex.commission || 0;
      totalFees += Number(ex.fees || 0);
      if (ex.transaction === "buy") totalBuys += ex.shares || 0;
      if (ex.transaction === "sell") totalSells += ex.shares || 0;
    });
  });

  return {
    count: trades.length,
    totalPnl,
    wins,
    losses,
    biggestWin: biggestWin.pnl === Number.NEGATIVE_INFINITY ? null : biggestWin,
    biggestLoss:
      biggestLoss.pnl === Number.POSITIVE_INFINITY ? null : biggestLoss,
    totalShares,
    totalCommissions,
    totalFees,
    totalBuys,
    totalSells,
  };
}

/** Build one merged trade-like object per symbol (for a single chart per symbol) */
function groupBySymbol(trades) {
  const map = new Map();
  trades.forEach((tr) => {
    const sym = tr.symbol?.toUpperCase() || "UNKNOWN";
    if (!map.has(sym)) map.set(sym, []);
    map.get(sym).push(tr);
  });

  const groups = [];
  for (const [symbol, symbolTrades] of map.entries()) {
    const pnl = symbolTrades.reduce((s, t) => s + (t.pnl || 0), 0);
    const totalShares = symbolTrades.reduce(
      (s, t) => s + (t.totalShares || 0),
      0
    );

    const mergedExecs = symbolTrades.flatMap((t) => t.singleTrades || []);
    mergedExecs.sort((a, b) =>
      String(a.dateTime).localeCompare(String(b.dateTime))
    );

    const totalCommissions = mergedExecs.reduce(
      (s, e) => s + (e.commission || 0),
      0
    );
    const totalFees = mergedExecs.reduce((s, e) => s + Number(e.fees || 0), 0);

    const openDate = symbolTrades.map((t) => String(t.openDate)).sort()[0];
    const closedDate = symbolTrades
      .map((t) => String(t.closedDate))
      .sort()
      .slice(-1)[0];

    const wins = symbolTrades.filter((t) => (t.pnl || 0) > 0).length;
    const losses = symbolTrades.filter((t) => (t.pnl || 0) < 0).length;

    const mergedTradeLike = {
      symbol,
      openDate,
      closedDate,
      pnl,
      totalShares,
      singleTrades: mergedExecs,
      stats: {
        totalSharesBought: mergedExecs
          .filter((e) => e.transaction === "buy")
          .reduce((s, e) => s + (e.shares || 0), 0),
        totalAmountBought: mergedExecs
          .filter((e) => e.transaction === "buy")
          .reduce((s, e) => s + (e.shares || 0) * (e.price || 0), 0),
        totalSharesSold: mergedExecs
          .filter((e) => e.transaction === "sell")
          .reduce((s, e) => s + (e.shares || 0), 0),
        totalAmountSold: mergedExecs
          .filter((e) => e.transaction === "sell")
          .reduce((s, e) => s + (e.shares || 0) * (e.price || 0), 0),
        accPnl: pnl,
      },
    };

    groups.push({
      symbol,
      pnl,
      wins,
      losses,
      totalShares,
      totalCommissions,
      totalFees,
      openDate,
      closedDate,
      mergedTradeLike,
      trades: symbolTrades,
    });
  }

  groups.sort((a, b) => Math.abs(b.pnl) - Math.abs(a.pnl));
  return groups;
}

export default function TradeDayDetail() {
  const { date } = useParams(); // expects YYYY-MM-DD
  const dispatch = useDispatch();
  const { config } = useAppSelector((state) => state.user);
  const { isLoading, isSuccess, tradeData } = useAppSelector(
    (state) => state.trade
  );
  const { defaultAccount } = config;

  const [expandedKeys, setExpandedKeys] = useState({});
  const toggleExpand = (key) =>
    setExpandedKeys((prev) => ({ ...prev, [key]: !prev[key] }));

  useEffect(() => {
    if (defaultAccount) {
      dispatch(fetchTrade({ pg: 1, accId: defaultAccount }));
    }
  }, [defaultAccount, dispatch]);

  const allTrades = useMemo(() => {
    if (!tradeData) return [];
    return Object.values(tradeData).filter(Boolean);
  }, [tradeData]);

  const tradesForDay = useMemo(() => {
    if (!date) return [];
    return allTrades.filter((t) => matchesDate(date, t));
  }, [allTrades, date]);

  const groupedBySymbol = useMemo(
    () => groupBySymbol(tradesForDay),
    [tradesForDay]
  );

  const daySummary = useMemo(() => summarizeDay(tradesForDay), [tradesForDay]);

  const dayLabel = useMemo(() => {
    if (!date) return "";
    return dayjs(date, "YYYY-MM-DD").format("MMM D, YYYY");
  }, [date]);

  return (
    <>
      {isLoading && !isSuccess && (
        <Spinner
          as="span"
          animation="border"
          variant="dark"
          role="status"
          aria-hidden="true"
        />
      )}

      {isSuccess && (
        <Container className="container-custom">
          {/* Top summary */}
          <Row
            xs={1}
            sm="auto"
            className="g-4"
            style={{ justifyContent: "center" }}
          >
            <Col>
              <Card className="tradeCard-boxShadow">
                <Card.Header className="card-header-custom trade-details">
                  Trades — {dayLabel}
                </Card.Header>
                <Card.Body>
                  <ListGroup variant="flush">
                    <ListGroup.Item>
                      <strong>Total Trades:</strong> {daySummary.count}
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <strong>Day P&amp;L:</strong>{" "}
                      <span
                        className={
                          daySummary.totalPnl >= 0
                            ? "pnl-positive"
                            : "pnl-negative"
                        }
                      >
                        ${daySummary.totalPnl.toFixed(2)}
                      </span>
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <strong>Wins/ Losses:</strong>{" "}
                      <span className="pnl-positive">{daySummary.wins}</span> /{" "}
                      <span className="pnl-negative">{daySummary.losses}</span>
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <strong>Biggest Win:</strong>{" "}
                      {daySummary.biggestWin ? (
                        <>
                          <span className="pnl-positive">
                            ${daySummary.biggestWin.pnl.toFixed(2)}
                          </span>{" "}
                          <Badge bg="success">
                            {daySummary.biggestWin.symbol}
                          </Badge>
                        </>
                      ) : (
                        "—"
                      )}
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <strong>Biggest Loss:</strong>{" "}
                      {daySummary.biggestLoss ? (
                        <>
                          <span className="pnl-negative">
                            ${daySummary.biggestLoss.pnl.toFixed(2)}
                          </span>{" "}
                          <Badge bg="danger">
                            {daySummary.biggestLoss.symbol}
                          </Badge>
                        </>
                      ) : (
                        "—"
                      )}
                    </ListGroup.Item>
                  </ListGroup>
                </Card.Body>
              </Card>
            </Col>

            <Col>
              <Card className="tradeCard-boxShadow statistics">
                <Card.Header className="card-header-custom statistics">
                  Statistics
                </Card.Header>
                <Card.Body>
                  <ListGroup variant="flush">
                    <ListGroup.Item>
                      <strong>Total Buys (shares):</strong>{" "}
                      {daySummary.totalBuys}
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <strong>Total Sells (shares):</strong>{" "}
                      {daySummary.totalSells}
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <strong>Total Commissions:</strong> $
                      {daySummary.totalCommissions.toFixed(2)}
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <strong>Total Fees:</strong> $
                      {daySummary.totalFees.toFixed(2)}
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <strong>Total Shares (position-level sum):</strong>{" "}
                      {daySummary.totalShares}
                    </ListGroup.Item>
                  </ListGroup>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Grouped-by-symbol, collapsible trade cards */}
          <Row className="mt-4">
            <Col>
              {groupedBySymbol.length === 0 ? (
                <Card className="tradeCard-boxShadow">
                  <Card.Body>No trades found for {dayLabel}.</Card.Body>
                </Card>
              ) : (
                groupedBySymbol.map((group, idx) => {
                  const key = `${group.symbol}-${group.openDate}-${idx}`;
                  const isExpanded = !!expandedKeys[key];
                  const panelId = `${key}-panel`;

                  return (
                    <Card className="tradeCard-boxShadow mb-4" key={key}>
                      {/* Clickable header toggles collapse */}
                      <Card.Header
                        className="card-header-custom d-flex justify-content-between align-items-center symbol-card-header"
                        role="button"
                        tabIndex={0}
                        aria-expanded={isExpanded}
                        aria-controls={panelId}
                        onClick={() =>
                          setExpandedKeys((prev) => ({
                            ...prev,
                            [key]: !prev[key],
                          }))
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setExpandedKeys((prev) => ({
                              ...prev,
                              [key]: !prev[key],
                            }));
                          }
                        }}
                      >
                        <div className="d-flex align-items-center gap-2">
                          <strong>{group.symbol}</strong>
                          <Badge
                            bg={group.pnl >= 0 ? "success" : "danger"}
                            style={{ fontSize: "0.85rem" }}
                          >
                            ${group.pnl.toFixed(2)}
                          </Badge>
                          <Badge bg="dark" style={{ fontSize: "0.85rem" }}>
                            <span className={"pnl-positive"}>
                              {group.wins}W
                            </span>{" "}
                            /{" "}
                            <span className={"pnl-negative"}>
                              {group.losses}L
                            </span>{" "}
                            • Trades: {group.trades.length}
                          </Badge>
                        </div>

                        <div className="d-flex gap-3 align-items-center">
                          <small>
                            Window:{" "}
                            {getDateAndTime(group.openDate).toLocaleString()} •{" "}
                            {getDateAndTime(group.closedDate).toLocaleString()}
                          </small>
                          <Button
                            size="sm"
                            variant="outline-light"
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedKeys((prev) => ({
                                ...prev,
                                [key]: !prev[key],
                              }));
                            }}
                          >
                            {isExpanded ? "Collapse" : "Expand"}
                          </Button>
                        </div>
                      </Card.Header>

                      <Collapse in={isExpanded}>
                        <div id={panelId}>
                          <Card.Body>
                            {/* Per-symbol summary + executions */}
                            <Row xs={1} md={2} className="g-4">
                              <Col>
                                <Card className="tradeCard-boxShadow">
                                  <Card.Header className="card-header-custom trade-details">
                                    Symbol Summary
                                  </Card.Header>
                                  <Card.Body>
                                    <ListGroup variant="flush">
                                      <ListGroup.Item>
                                        <strong>Total P&amp;L:</strong>{" "}
                                        <span
                                          className={
                                            group.pnl >= 0
                                              ? "pnl-positive"
                                              : "pnl-negative"
                                          }
                                        >
                                          ${group.pnl.toFixed(2)}
                                        </span>
                                      </ListGroup.Item>
                                      <ListGroup.Item>
                                        <strong>
                                          Total Shares (positions):
                                        </strong>{" "}
                                        {group.totalShares}
                                      </ListGroup.Item>
                                      <ListGroup.Item>
                                        <strong>Total Commissions:</strong> $
                                        {group.totalCommissions.toFixed(2)}
                                      </ListGroup.Item>
                                      <ListGroup.Item>
                                        <strong>Total Fees:</strong> $
                                        {group.totalFees.toFixed(2)}
                                      </ListGroup.Item>
                                      <ListGroup.Item>
                                        <strong>
                                          Held Duration (full window):
                                        </strong>{" "}
                                        {calculateHeldDuration(
                                          group.openDate,
                                          group.closedDate
                                        )}
                                      </ListGroup.Item>
                                    </ListGroup>
                                  </Card.Body>
                                </Card>
                              </Col>

                              <Col>
                                <Card className="tradeCard-boxShadow">
                                  <Card.Header className="card-header-custom">
                                    Individual Trades ({group.symbol})
                                  </Card.Header>
                                  <Card.Body style={{ overflow: "auto" }}>
                                    <Table striped bordered hover>
                                      <thead>
                                        <tr>
                                          <th>#</th>
                                          <th>Txn</th>
                                          <th>P&amp;L</th>
                                          <th>Avg Buy</th>
                                          <th>Avg Sell</th>
                                          <th>Shares</th>
                                          <th>Open</th>
                                          <th>Close</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {[...group.trades]
                                          .sort(
                                            (a, b) =>
                                              Number(a.openDate) -
                                              Number(b.openDate)
                                          )
                                          .map((tr, i) => (
                                            <tr
                                              key={
                                                tr._id ||
                                                `${group.symbol}-tr-${i}`
                                              }
                                            >
                                              <td>{i + 1}</td>
                                              <td>
                                                <Badge
                                                  bg={
                                                    tr.transaction?.toUpperCase() ===
                                                    "BUY"
                                                      ? "success"
                                                      : "danger"
                                                  }
                                                >
                                                  {tr.transaction?.toUpperCase() ===
                                                  "BUY"
                                                    ? "Long"
                                                    : "Short"}
                                                </Badge>
                                              </td>
                                              <td
                                                className={
                                                  tr.pnl >= 0
                                                    ? "pnl-positive"
                                                    : "pnl-negative"
                                                }
                                              >
                                                ${tr.pnl.toFixed(2)}
                                              </td>
                                              <td>
                                                ${tr.avgBuyPrice.toFixed(2)}
                                              </td>
                                              <td>
                                                ${tr.avgSellPrice.toFixed(2)}
                                              </td>
                                              <td>{tr.totalShares}</td>
                                              <td>
                                                {dayjs(
                                                  String(tr.openDate),
                                                  "YYYYMMDDHHmmss"
                                                ).format("HH:mm:ss")}
                                              </td>
                                              <td>
                                                {dayjs(
                                                  String(tr.closedDate),
                                                  "YYYYMMDDHHmmss"
                                                ).format("HH:mm:ss")}
                                              </td>
                                            </tr>
                                          ))}
                                      </tbody>
                                    </Table>
                                  </Card.Body>
                                </Card>
                              </Col>
                            </Row>

                            {/* Per-symbol chart */}
                            <Row className="mt-4">
                              <Col>
                                <Card className="tradeCard-boxShadow executions">
                                  <Card.Header className="card-header-custom executions">
                                    {group.symbol} — Historical Chart
                                  </Card.Header>
                                  <Card.Body
                                    style={{
                                      overflow: "auto",
                                      backgroundColor: "#212f2f",
                                    }}
                                  >
                                    <div style={{ backgroundColor: "#191c27" }}>
                                      <CandleStickChart
                                        trades={group.mergedTradeLike}
                                      />
                                    </div>
                                  </Card.Body>
                                </Card>
                              </Col>
                            </Row>

                            <Row className="mt-4">
                              <Col>
                                <Card className="tradeCard-boxShadow statistics">
                                  <Card.Header className="card-header-custom statistics">
                                    All Executions ({group.symbol})
                                  </Card.Header>
                                  <Card.Body style={{ overflow: "auto" }}>
                                    <Table
                                      striped
                                      bordered
                                      hover
                                      className="table-executions"
                                    >
                                      <thead>
                                        <tr>
                                          <th>#</th>
                                          <th>Side</th>
                                          <th>Shares</th>
                                          <th>Price</th>
                                          <th>Commission</th>
                                          <th>Fees</th>
                                          <th>Broker</th>
                                          <th>Date Time</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {group.mergedTradeLike.singleTrades.map(
                                          (ex, i) => (
                                            <tr
                                              key={ex._id || `${key}-ex-${i}`}
                                            >
                                              <td>{i + 1}</td>
                                              <td>
                                                {ex.transaction?.toUpperCase()}
                                              </td>
                                              <td>{ex.shares}</td>
                                              <td>
                                                ${(ex.price || 0).toFixed(2)}
                                              </td>
                                              <td>
                                                $
                                                {(ex.commission || 0).toFixed(
                                                  2
                                                )}
                                              </td>
                                              <td>${Number(ex.fees || 0)}</td>
                                              <td>{ex.broker}</td>
                                              <td>
                                                {dayjs(
                                                  String(ex.dateTime),
                                                  "YYYYMMDDHHmmss"
                                                ).format("HH:mm:ss")}
                                              </td>
                                            </tr>
                                          )
                                        )}
                                      </tbody>
                                    </Table>
                                  </Card.Body>
                                </Card>
                              </Col>
                            </Row>
                          </Card.Body>
                        </div>
                      </Collapse>
                    </Card>
                  );
                })
              )}
            </Col>
          </Row>
        </Container>
      )}
    </>
  );
}
